import { COURSE_HREF } from '../../lib/landing';
import { courseOverview, type OverviewCard } from '../../lib/course-overview';
import { landingFrontCopy } from '../../lib/strings/landing-front';
import { useLocale } from '../../state/useLocale';
import type { PublicMission } from '../../types';
import { LinkButton } from '../../ui';

/**
 * What the course is, for the person rather than their agent (owner,
 * 2026-10-31: "there is nothing for the humans to see"): how it works in
 * three steps, then what you work through, the general track's worlds in five
 * groups and the specialization tracks after them, each with its real level
 * count from the catalogue (lib/course-overview.ts). It replaces the folded
 * curriculum grid; every level is still one link away on the ledger.
 */
export function CourseOverview({ missions }: { missions: PublicMission[] }) {
  const { locale } = useLocale();
  const s = landingFrontCopy[locale];
  const overview = courseOverview(missions);
  const card = (c: OverviewCard<keyof typeof s.groups>) => {
    const copy = s.groups[c.id];
    return (
      <li key={c.id} className="course-overview__card">
        <div className="course-overview__top">
          <h4>{copy.title}</h4>
          <span className="course-overview__count">{s.tasks(c.levels)}</span>
        </div>
        <p>{copy.body}</p>
        <ul className="course-overview__worlds">{c.worlds.map((w) => <li key={w}>{w}</li>)}</ul>
      </li>
    );
  };
  return (
    <section className="course-overview" id="course" aria-labelledby="course-overview-title">
      <div className="course-overview__head">
        <p className="label">{s.courseLabel}</p>
        <h2 id="course-overview-title">{s.courseTitle}</h2>
        <p className="lede">{s.courseLede}</p>
      </div>

      <h3 className="course-overview__h3">{s.howTitle}</h3>
      <ol className="course-overview__steps">
        {s.steps.map(([title, body], i) => (
          <li key={title}>
            <span className="course-overview__step" aria-hidden="true">{i + 1}</span>
            <h4>{title}</h4>
            <p>{body}</p>
          </li>
        ))}
      </ol>

      {overview.groups.length > 0 && (
        <>
          <h3 className="course-overview__h3">{s.groupsTitle}</h3>
          <ul className="course-overview__grid">{overview.groups.map(card)}</ul>
        </>
      )}
      {overview.tracks.length > 0 && (
        <>
          <h3 className="course-overview__h3">{s.tracksTitle}</h3>
          <ul className="course-overview__grid is-tracks">{overview.tracks.map(card)}</ul>
        </>
      )}
      <div className="course-overview__foot">
        {overview.checkpoints > 0 && <p className="note">{s.checkpoints(overview.checkpoints)}</p>}
        {overview.levels > 0 && <LinkButton href={'/' + COURSE_HREF}>{s.allLevels(overview.levels)}</LinkButton>}
      </div>
    </section>
  );
}

export default CourseOverview;
